import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { moneySchema, toMoney } from '@/shared/api/schemas'
import { downloadFile } from '@/shared/lib/fileDownload'
import type { Money } from '@/shared/lib/money'
import type { HistoryPeriod } from './historyPeriod'

export const HISTORY_PAGE_SIZE = 50

const actorSchema = z.object({
  type: z.string(),
  name: z.string().nullish(),
})

const rowSchema = z.object({
  txnId: z.number(),
  at: z.string(),
  kind: z.string(),
  amount: moneySchema,
  balanceAfter: moneySchema,
  trafficBytes: z.number().nullish(),
  reference: z.string().nullish(),
  note: z.string().nullish(),
  packageName: z.string().nullish(),
  actor: actorSchema.nullish(),
})

const pageSchema = z.object({
  from: z.string(),
  to: z.string(),
  opening: moneySchema,
  closing: moneySchema,
  rows: z.array(rowSchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

const statementSchema = z.object({
  company: z
    .object({ name: z.string().nullish(), logoUrl: z.string().nullish() })
    .nullish(),
  customer: z.object({
    login: z.string(),
    name: z.string().nullish(),
    customerId: z.string().nullish(),
  }),
  from: z.string(),
  to: z.string(),
  timezone: z.string().nullish(),
  opening: moneySchema,
  closing: moneySchema,
  rows: z.array(rowSchema),
  truncated: z.boolean().nullish(),
  generatedAt: z.string(),
})

export interface HistoryActor {
  type: string
  name: string | null
}

export interface HistoryRow {
  txnId: number
  at: string
  kind: string
  amount: Money
  balanceAfter: Money
  trafficBytes: number
  reference: string | null
  note: string | null
  packageName: string | null
  actor: HistoryActor | null
}

export interface HistoryPage {
  period: HistoryPeriod
  opening: Money
  closing: Money
  rows: HistoryRow[]
  page: number
  hasNext: boolean
}

export interface Statement {
  company: { name: string | null; logoUrl: string | null }
  customer: { login: string; name: string | null; customerId: string | null }
  period: HistoryPeriod
  opening: Money
  closing: Money
  rows: HistoryRow[]
  truncated: boolean
  generatedAt: string
}

function toRow(raw: z.infer<typeof rowSchema>): HistoryRow {
  return {
    txnId: raw.txnId,
    at: raw.at,
    kind: raw.kind,
    amount: toMoney(raw.amount),
    balanceAfter: toMoney(raw.balanceAfter),
    trafficBytes: raw.trafficBytes ?? 0,
    reference: raw.reference ?? null,
    note: raw.note ?? null,
    packageName: raw.packageName ?? null,
    actor: raw.actor ? { type: raw.actor.type, name: raw.actor.name ?? null } : null,
  }
}

export function parseHistoryPage(data: unknown): HistoryPage {
  const raw = pageSchema.parse(data)
  return {
    period: { from: raw.from, to: raw.to },
    opening: toMoney(raw.opening),
    closing: toMoney(raw.closing),
    rows: raw.rows.map(toRow),
    page: raw.page,
    hasNext: raw.hasNext,
  }
}

export function parseStatement(data: unknown): Statement {
  const raw = statementSchema.parse(data)
  return {
    company: { name: raw.company?.name ?? null, logoUrl: raw.company?.logoUrl ?? null },
    customer: {
      login: raw.customer.login,
      name: raw.customer.name ?? null,
      customerId: raw.customer.customerId ?? null,
    },
    period: { from: raw.from, to: raw.to },
    opening: toMoney(raw.opening),
    closing: toMoney(raw.closing),
    rows: raw.rows.map(toRow),
    truncated: raw.truncated ?? false,
    generatedAt: raw.generatedAt,
  }
}

const historyPath = (subscriberId: string) =>
  `/api/subscribers/${encodeURIComponent(subscriberId)}/history`

export async function fetchHistoryPage(
  subscriberId: string,
  period: HistoryPeriod,
  page: number,
  signal?: AbortSignal,
): Promise<HistoryPage> {
  const response = await api.get<unknown>(historyPath(subscriberId), {
    params: { ...period, page, size: HISTORY_PAGE_SIZE },
    ...(signal ? { signal } : {}),
  })
  return parseHistoryPage(response.data)
}

export async function fetchStatement(subscriberId: string, period: HistoryPeriod): Promise<Statement> {
  const response = await api.get<unknown>(`${historyPath(subscriberId)}/statement`, { params: period })
  return parseStatement(response.data)
}

export function downloadHistoryExcel(
  subscriberId: string,
  login: string,
  period: HistoryPeriod,
): Promise<void> {
  return downloadFile(
    `${historyPath(subscriberId)}/export`,
    { format: 'xlsx', ...period },
    `history-${login}-${period.from}-${period.to}.xlsx`,
  )
}

export function isOutOfScope(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404
}
